import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istFehlendesSchema } from './sifaService';
import { parseIsoDatum } from './fristenLogic';
import type {
  ArbeitsbereichEntwurf,
  ArbeitsmittelEntwurf,
  ArbeitsmittelGestelltVon,
  Kataster,
  SifaArbeitsbereich,
  SifaArbeitsmittel,
  SifaMitarbeiterBereich,
  SifaStoff,
  Signalwort,
  StoffArt,
  StoffEntwurf,
} from './gbuTypes';

/**
 * Datenbankzugriff des BETRIEBSKATASTERS je SiFa-Projekt — Tabellen
 * `sifa_arbeitsbereiche`, `sifa_mitarbeiter_bereiche`, `sifa_arbeitsmittel`
 * und `sifa_stoffe` (supabase/sifa_gbu.sql, MIGRATIONS.md Nr. 167). Vertrag:
 * docs/sifa-gbu-plan.md §4.2 und §14.4 C; GBU-Editor, Vorsorge und
 * Gefahrstoffverzeichnis lesen dasselbe Kataster.
 *
 * Muster sifaBetreuungService.ts, mit zwei Riegeln:
 *
 * (a) IN BENUTZUNG WIRD NICHT GELOESCHT. Ein Arbeitsbereich mit GBU haelt
 *     der Fremdschluessel `sifa_gbu.bereich_id … on delete restrict` (23001
 *     → Klartext). Arbeitsmittel und Stoffe haengen nur ueber uuid-Listen an
 *     den Gefaehrdungen (`arbeitsmittel_ids`, `stoff_ids`, kein
 *     Fremdschluessel) — dort zaehlt der Client vor dem Loeschen. Aus dem
 *     Bezug entstehen BA-Bedarf und Gefahrstoffverzeichnis; ein geloeschter
 *     Stoff liesse dort eine Luecke ohne Namen. Statt loeschen: `aktiv` aus.
 *
 * (b) ZUORDNUNGEN WERDEN GESCHLOSSEN, NICHT GELOESCHT. Wer einen Bereich
 *     verlaesst, bekommt `bis = heute`; die Zeile bleibt. Die Vorsorge je
 *     Person (Stufe 2) fragt spaeter, wer WANN wo taetig war.
 *
 * FAIL-OPEN: solange Nr. 167 nicht gefahren ist, liefert `ladeKataster`
 * leere Listen mit `schemaFehlt: true` (42P01/PGRST205). Eine offene
 * Migration darf die Projektansicht nicht aufreissen. Schreibwege werfen.
 *
 * Alle Fehler gehen als `Error(describeError(e))` hinaus.
 */

const T_BEREICHE = 'sifa_arbeitsbereiche';
const T_ZUORDNUNGEN = 'sifa_mitarbeiter_bereiche';
const T_ARBEITSMITTEL = 'sifa_arbeitsmittel';
const T_STOFFE = 'sifa_stoffe';
const T_GEFAEHRDUNGEN = 'sifa_gbu_gefaehrdungen';

const GESTELLT_VON: readonly ArbeitsmittelGestelltVon[] = ['eigen', 'auftraggeber', 'verleiher'];
const STOFF_ARTEN: readonly StoffArt[] = ['gefahrstoff', 'biostoff'];
const SIGNALWOERTER: readonly Signalwort[] = ['gefahr', 'achtung'];

// ─── Fehler ─────────────────────────────────────────────────────────────────

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** 23505 = unique_violation, hier: die Person ist dem Bereich schon (offen) zugeordnet. */
const istUniqueVerletzung = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Eine GBU haelt den Arbeitsbereich (`sifa_gbu_bereich_fk … on delete
 * restrict`): Postgres meldet das als 23001 (restrict_violation); 23503
 * (foreign_key_violation) kommt bei `no action` — beides heisst „haengt noch dran".
 */
const istFremdschluesselSperre = (e: unknown): boolean => errorCode(e) === '23001' || errorCode(e) === '23503';

const werfe = (e: unknown): never => {
  if (istFehlendesSchema(e)) throw new Error('Das Betriebskataster ist noch nicht eingerichtet (Migration Nr. 167 fehlt).');
  throw new Error(describeError(e));
};

// ─── Mapper snake → camel ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null || v === '' ? null : num(v, 0));
const strListe = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => str(x)).filter(Boolean) : []);
/** Datum/Zeitstempel ohne Umrechnung — die Anzeige formatiert selbst. */
const iso = (v: unknown): string => str(v);

export const mapArbeitsbereich = (row: Zeile): SifaArbeitsbereich => ({
  id: str(row.id),
  projectId: str(row.project_id),
  bezeichnung: str(row.bezeichnung),
  bereich: strOrNull(row.bereich),
  beschreibung: strOrNull(row.beschreibung),
  ort: strOrNull(row.ort),
  aktiv: row.aktiv !== false,
  sortOrder: num(row.sort_order, 0),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapMitarbeiterBereich = (row: Zeile): SifaMitarbeiterBereich => ({
  id: str(row.id),
  projectId: str(row.project_id),
  mitarbeiterId: str(row.mitarbeiter_id),
  bereichId: str(row.bereich_id),
  seit: strOrNull(row.seit),
  bis: strOrNull(row.bis),
});

export const mapArbeitsmittel = (row: Zeile): SifaArbeitsmittel => ({
  id: str(row.id),
  projectId: str(row.project_id),
  bezeichnung: str(row.bezeichnung),
  typ: strOrNull(row.typ),
  hersteller: strOrNull(row.hersteller),
  gestelltVon: (GESTELLT_VON as readonly string[]).includes(str(row.gestellt_von))
    ? (str(row.gestellt_von) as ArbeitsmittelGestelltVon)
    : 'eigen',
  pruefung: strOrNull(row.pruefung),
  pruefIntervallMonate: numOrNull(row.pruef_intervall_monate),
  pruefer: strOrNull(row.pruefer),
  notiz: strOrNull(row.notiz),
  aktiv: row.aktiv !== false,
  sortOrder: num(row.sort_order, 0),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

export const mapStoff = (row: Zeile): SifaStoff => ({
  id: str(row.id),
  projectId: str(row.project_id),
  art: str(row.art) === 'biostoff' ? 'biostoff' : 'gefahrstoff',
  bezeichnung: str(row.bezeichnung),
  hersteller: strOrNull(row.hersteller),
  cas: strOrNull(row.cas),
  signalwort: (SIGNALWOERTER as readonly string[]).includes(str(row.signalwort)) ? (str(row.signalwort) as Signalwort) : null,
  hSaetze: strListe(row.h_saetze),
  ghs: strListe(row.ghs),
  risikogruppe: numOrNull(row.risikogruppe),
  mengeJahr: strOrNull(row.menge_jahr),
  sdbDatum: strOrNull(row.sdb_datum),
  sdbPfad: strOrNull(row.sdb_pfad),
  substitutionGeprueftAm: strOrNull(row.substitution_geprueft_am),
  substitutionErgebnis: strOrNull(row.substitution_ergebnis),
  notiz: strOrNull(row.notiz),
  aktiv: row.aktiv !== false,
  sortOrder: num(row.sort_order, 0),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

// ─── Hilfen ─────────────────────────────────────────────────────────────────

/** Der Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return name?.trim() ? name.trim() : null;
  } catch {
    // Anzeige, kein Riegel — das Anlegen haengt nicht an einer Nebensache.
    return null;
  }
};

/** Der uebergebene Firmenname, sonst der des Profils. */
const firmaFuer = async (companyName: string | null | undefined): Promise<string | null> =>
  leerZuNull(companyName) ?? (await eigenerFirmenname());

/** Leere Zeichenkette → null (Spalten sind NULL-bar; ein '' ist dort eine Luege). */
const leerZuNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

/** Pflichttext; wirft mit dem Feldnamen. */
const pflicht = (v: string | null | undefined, meldung: string): string => {
  const t = (v ?? '').trim();
  if (!t) throw new Error(meldung);
  return t;
};

/** ISO-Datum oder null; ein kaputtes Datum wirft mit Feldname (nie `new Date`). */
const datumOderNull = (v: string | null | undefined, feld: string): string | null => {
  const t = (v ?? '').trim();
  if (!t) return null;
  if (!parseIsoDatum(t)) throw new Error(`Bitte ein gültiges Datum für „${feld}" eingeben.`);
  return t.slice(0, 10);
};

/** Codes wie H315 oder GHS05: getrimmt, gross, ohne Dubletten. */
const codeListe = (v: string[] | null | undefined): string[] => {
  const gesehen = new Set<string>();
  const aus: string[] = [];
  for (const roh of v ?? []) {
    const c = (roh ?? '').trim().toUpperCase();
    if (!c || gesehen.has(c)) continue;
    gesehen.add(c);
    aus.push(c);
  }
  return aus;
};

/** Hoechste sort_order des Projekts + 1 — neue Zeilen kommen ans Ende. */
const naechsteSortierung = async (tabelle: string, projectId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(tabelle)
    .select('sort_order')
    .eq('project_id', projectId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return werfe(error);
  const hoechste = Number((data as { sort_order?: unknown } | null)?.sort_order);
  return Number.isFinite(hoechste) ? hoechste + 1 : 0;
};

/**
 * Gemeinsamer Schreibweg: ohne `id` anlegen (am Ende einsortiert, mit
 * Firma), mit `id` aendern — `sort_order`, Projekt und Firma bleiben dann.
 * Das Update traegt `project_id` als zweiten Filter: eine id aus einem
 * anderen Projekt trifft nichts.
 */
const schreibe = async (
  tabelle: string,
  projectId: string,
  felder: Zeile,
  id: string | undefined,
  companyName: string | null | undefined,
  nichtGefunden: string,
): Promise<Zeile> => {
  const vid = (id ?? '').trim();
  if (vid) {
    const { data, error } = await supabase
      .from(tabelle)
      .update({ ...felder, updated_at: new Date().toISOString() })
      .eq('id', vid)
      .eq('project_id', projectId)
      .select('*');
    if (error) return werfe(error);
    if (!data || data.length === 0) throw new Error(nichtGefunden);
    return data[0] as Zeile;
  }
  const [sortOrder, company] = await Promise.all([naechsteSortierung(tabelle, projectId), firmaFuer(companyName)]);
  const { data, error } = await supabase
    .from(tabelle)
    .insert({ ...felder, project_id: projectId, sort_order: sortOrder, company_name: company })
    .select('*')
    .single();
  if (error) return werfe(error);
  return data as Zeile;
};

const projektPflicht = (projectId: string, was: string): string => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error(`Ohne Projekt lässt sich ${was} nicht speichern.`);
  return pid;
};

// ═══ Lesen ════════════════════════════════════════════════════════════════

const leeresKataster = (schemaFehlt: boolean): Kataster => ({ bereiche: [], arbeitsmittel: [], stoffe: [], zuordnungen: [], schemaFehlt });

/**
 * Das ganze Kataster eines Projekts in vier parallelen Abfragen, je nach
 * `sort_order` und Bezeichnung — auch inaktive Eintraege (die Pflegeansicht
 * zeigt sie ausgegraut) und geschlossene Zuordnungen (`bis` gesetzt, fuer die
 * Vorsorge je Person). Wer nur die aktiven will, filtert.
 *
 * Fail-open: fehlt EINE der Tabellen, ist Nr. 167 nicht gefahren — dann
 * leere Listen und `schemaFehlt: true`. RLS antwortet mit LEERER Menge, nicht
 * mit Fehler.
 */
export const ladeKataster = async (projectId: string): Promise<Kataster> => {
  const pid = (projectId || '').trim();
  if (!pid) return leeresKataster(false);

  const lies = async (tabelle: string, sortiert: boolean): Promise<{ zeilen: Zeile[]; fehlt: boolean }> => {
    try {
      let q = supabase.from(tabelle).select('*').eq('project_id', pid);
      q = sortiert
        ? q.order('sort_order', { ascending: true }).order('bezeichnung', { ascending: true })
        : q.order('seit', { ascending: true }).order('id', { ascending: true });
      const { data, error } = await q;
      if (error) throw error;
      return { zeilen: (data ?? []) as Zeile[], fehlt: false };
    } catch (e) {
      if (istFehlendesSchema(e)) return { zeilen: [], fehlt: true };
      return werfe(e);
    }
  };

  const [bereiche, arbeitsmittel, stoffe, zuordnungen] = await Promise.all([
    lies(T_BEREICHE, true),
    lies(T_ARBEITSMITTEL, true),
    lies(T_STOFFE, true),
    lies(T_ZUORDNUNGEN, false),
  ]);

  if (bereiche.fehlt || arbeitsmittel.fehlt || stoffe.fehlt || zuordnungen.fehlt) {
    console.warn('Betriebskataster fehlt — Migration Nr. 167 noch nicht gefahren.');
    return leeresKataster(true);
  }
  return {
    bereiche: bereiche.zeilen.map(mapArbeitsbereich),
    arbeitsmittel: arbeitsmittel.zeilen.map(mapArbeitsmittel),
    stoffe: stoffe.zeilen.map(mapStoff),
    zuordnungen: zuordnungen.zeilen.map(mapMitarbeiterBereich),
    schemaFehlt: false,
  };
};

// ═══ Arbeitsbereiche ══════════════════════════════════════════════════════

/** Legt einen Arbeitsbereich an (ohne `id`) oder aendert ihn. Bezeichnung ist Pflicht. */
export const speichereArbeitsbereich = async (
  projectId: string,
  entwurf: ArbeitsbereichEntwurf,
  id?: string,
  companyName?: string | null,
): Promise<SifaArbeitsbereich> => {
  const pid = projektPflicht(projectId, 'ein Arbeitsbereich');
  const felder: Zeile = {
    bezeichnung: pflicht(entwurf?.bezeichnung, 'Bitte eine Bezeichnung für den Arbeitsbereich eingeben.'),
    bereich: leerZuNull(entwurf.bereich),
    beschreibung: leerZuNull(entwurf.beschreibung),
    ort: leerZuNull(entwurf.ort),
    aktiv: entwurf.aktiv !== false,
  };
  const zeile = await schreibe(T_BEREICHE, pid, felder, id, companyName, 'Dieser Arbeitsbereich wurde nicht gefunden (Speichern).');
  return mapArbeitsbereich(zeile);
};

const MELDUNG_BEREICH_IN_GBU =
  'Dieser Arbeitsbereich hat eine Gefährdungsbeurteilung und lässt sich nicht löschen — bitte auf „inaktiv" setzen.';

/**
 * Loescht einen Arbeitsbereich samt seiner Personen-Zuordnungen (Kaskade).
 * Haelt ihn eine GBU (restrict, Riegel a), kommt der Klartext statt 23001.
 * Ein nicht (mehr) vorhandener ist kein Fehler.
 */
export const loescheArbeitsbereich = async (id: string): Promise<void> => {
  const bid = (id || '').trim();
  if (!bid) return;
  const { error } = await supabase.from(T_BEREICHE).delete().eq('id', bid);
  if (!error) return;
  if (istFremdschluesselSperre(error)) throw new Error(MELDUNG_BEREICH_IN_GBU);
  return werfe(error);
};

// ═══ Arbeitsmittel ════════════════════════════════════════════════════════

const pruefeIntervall = (v: number | null | undefined): number | null => {
  if (v == null) return null;
  if (!Number.isInteger(v) || v < 1 || v > 120) {
    throw new Error('Das Prüfintervall muss zwischen 1 und 120 Monaten liegen (leer = keins).');
  }
  return v;
};

/**
 * Legt ein Arbeitsmittel an (ohne `id`) oder aendert es. `gestelltVon`
 * entscheidet spaeter, ob die App eine eigene Prueffrist anbietet
 * ('auftraggeber': Pruefnachweis beim AG) — hier nur gespeichert.
 */
export const speichereArbeitsmittel = async (
  projectId: string,
  entwurf: ArbeitsmittelEntwurf,
  id?: string,
  companyName?: string | null,
): Promise<SifaArbeitsmittel> => {
  const pid = projektPflicht(projectId, 'ein Arbeitsmittel');
  if (!GESTELLT_VON.includes(entwurf?.gestelltVon)) throw new Error('Bitte wählen, wer das Arbeitsmittel stellt.');
  const felder: Zeile = {
    bezeichnung: pflicht(entwurf.bezeichnung, 'Bitte eine Bezeichnung für das Arbeitsmittel eingeben.'),
    typ: leerZuNull(entwurf.typ),
    hersteller: leerZuNull(entwurf.hersteller),
    gestellt_von: entwurf.gestelltVon,
    pruefung: leerZuNull(entwurf.pruefung),
    pruef_intervall_monate: pruefeIntervall(entwurf.pruefIntervallMonate),
    pruefer: leerZuNull(entwurf.pruefer),
    notiz: leerZuNull(entwurf.notiz),
    aktiv: entwurf.aktiv !== false,
  };
  const zeile = await schreibe(T_ARBEITSMITTEL, pid, felder, id, companyName, 'Dieses Arbeitsmittel wurde nicht gefunden (Speichern).');
  return mapArbeitsmittel(zeile);
};

/**
 * Wie viele Gefaehrdungen auf den Eintrag zeigen (uuid-Liste `spalte`).
 * Fehlt die GBU-Tabelle, gibt es keine Verweise → 0.
 */
const zaehleVerweise = async (spalte: 'arbeitsmittel_ids' | 'stoff_ids', id: string): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from(T_GEFAEHRDUNGEN)
      .select('id', { count: 'exact', head: true })
      .contains(spalte, [id]);
    if (error) throw error;
    return typeof count === 'number' ? count : 0;
  } catch (e) {
    if (istFehlendesSchema(e)) return 0;
    return werfe(e);
  }
};

const verweisMeldung = (was: string, n: number): string =>
  `${was} wird in ${n === 1 ? 'einer Gefährdung' : `${n} Gefährdungen`} einer Gefährdungsbeurteilung genannt und lässt sich nicht löschen — bitte auf „inaktiv" setzen.`;

/** Loescht ein Arbeitsmittel — nur ohne Verweis aus einer Gefaehrdung (Riegel a). */
export const loescheArbeitsmittel = async (id: string): Promise<void> => {
  const aid = (id || '').trim();
  if (!aid) return;
  const verweise = await zaehleVerweise('arbeitsmittel_ids', aid);
  if (verweise > 0) throw new Error(verweisMeldung('Dieses Arbeitsmittel', verweise));
  const { error } = await supabase.from(T_ARBEITSMITTEL).delete().eq('id', aid);
  if (error) return werfe(error);
};

// ═══ Stoffe ═══════════════════════════════════════════════════════════════

const pruefeRisikogruppe = (art: StoffArt, v: number | null | undefined): number | null => {
  // Die Risikogruppe gibt es nur bei Biostoffen; bei Gefahrstoffen waere sie Unsinn.
  if (art !== 'biostoff' || v == null) return null;
  if (!Number.isInteger(v) || v < 1 || v > 4) throw new Error('Die Risikogruppe eines Biostoffs ist 1, 2, 3 oder 4.');
  return v;
};

/**
 * Legt einen Gefahr- oder Biostoff an (ohne `id`) oder aendert ihn.
 * H-Saetze und GHS-Codes werden normalisiert (gross, ohne Dubletten).
 * `sdbPfad` geht NICHT hierueber — das SDB kommt mit Stufe 3 (Upload).
 */
export const speichereStoff = async (
  projectId: string,
  entwurf: StoffEntwurf,
  id?: string,
  companyName?: string | null,
): Promise<SifaStoff> => {
  const pid = projektPflicht(projectId, 'ein Stoff');
  if (!STOFF_ARTEN.includes(entwurf?.art)) throw new Error('Bitte wählen: Gefahrstoff oder Biostoff.');
  const signalwort = entwurf.signalwort ?? null;
  if (signalwort !== null && !SIGNALWOERTER.includes(signalwort)) throw new Error('Das Signalwort ist „Gefahr" oder „Achtung" (oder leer).');
  const felder: Zeile = {
    art: entwurf.art,
    bezeichnung: pflicht(entwurf.bezeichnung, 'Bitte eine Bezeichnung für den Stoff eingeben.'),
    hersteller: leerZuNull(entwurf.hersteller),
    cas: leerZuNull(entwurf.cas),
    signalwort,
    h_saetze: codeListe(entwurf.hSaetze),
    ghs: codeListe(entwurf.ghs),
    risikogruppe: pruefeRisikogruppe(entwurf.art, entwurf.risikogruppe),
    menge_jahr: leerZuNull(entwurf.mengeJahr),
    sdb_datum: datumOderNull(entwurf.sdbDatum, 'Datum des Sicherheitsdatenblatts'),
    substitution_geprueft_am: datumOderNull(entwurf.substitutionGeprueftAm, 'Substitution geprüft am'),
    substitution_ergebnis: leerZuNull(entwurf.substitutionErgebnis),
    notiz: leerZuNull(entwurf.notiz),
    aktiv: entwurf.aktiv !== false,
  };
  const zeile = await schreibe(T_STOFFE, pid, felder, id, companyName, 'Dieser Stoff wurde nicht gefunden (Speichern).');
  return mapStoff(zeile);
};

/** Loescht einen Stoff — nur ohne Verweis aus einer Gefaehrdung (Riegel a). */
export const loescheStoff = async (id: string): Promise<void> => {
  const sid = (id || '').trim();
  if (!sid) return;
  const verweise = await zaehleVerweise('stoff_ids', sid);
  if (verweise > 0) throw new Error(verweisMeldung('Dieser Stoff', verweise));
  const { error } = await supabase.from(T_STOFFE).delete().eq('id', sid);
  if (error) return werfe(error);
};

// ═══ Personen ↔ Arbeitsbereich (E2) ═══════════════════════════════════════

/**
 * Setzt, wer im Arbeitsbereich taetig ist (Riegel b): wer neu in
 * `mitarbeiterIds` steht, bekommt eine Zeile `seit = heute, bis = null`;
 * wer in einer OFFENEN Zeile steht, aber nicht mehr in der Liste, bekommt
 * `bis = heute` — die Zeile bleibt. Unveraenderte bleiben unberuehrt.
 *
 * Eingefuegt wird einzeln: der partielle Unique-Index (mitarbeiter_id,
 * bereich_id) where bis is null meldet eine Zuordnung, die ein zweiter
 * Browser eben angelegt hat, mit 23505 — das ist das gewuenschte Ergebnis,
 * kein Fehler. Geschlossen wird nur `where bis is null`.
 *
 * `heute` als ISO-Datum (yyyy-mm-dd, Berliner Tag aus der Oberflaeche).
 * Rueckgabe: ALLE Zuordnungen des Bereichs (offen und geschlossen), frisch
 * gelesen — die Oberflaeche ersetzt damit ihre Zeilen dieses Bereichs.
 */
export const setzeBereichsMitarbeiter = async (
  projectId: string,
  bereichId: string,
  mitarbeiterIds: string[],
  heute: string,
): Promise<SifaMitarbeiterBereich[]> => {
  const pid = projektPflicht(projectId, 'eine Zuordnung');
  const bid = (bereichId || '').trim();
  if (!bid) throw new Error('Kein Arbeitsbereich gewählt.');
  const tag = datumOderNull(heute, 'heute');
  if (!tag) throw new Error('Bitte ein gültiges Datum für „heute" angeben.');
  const soll = new Set((mitarbeiterIds ?? []).map((x) => (x || '').trim()).filter(Boolean));

  const { data: offenRoh, error: leseFehler } = await supabase
    .from(T_ZUORDNUNGEN)
    .select('*')
    .eq('bereich_id', bid)
    .is('bis', null);
  if (leseFehler) return werfe(leseFehler);
  const offen = ((offenRoh ?? []) as Zeile[]).map(mapMitarbeiterBereich);
  const offenIds = new Set(offen.map((z) => z.mitarbeiterId));

  const neu = [...soll].filter((m) => !offenIds.has(m));
  const schliessen = offen.filter((z) => !soll.has(z.mitarbeiterId)).map((z) => z.id);

  if (neu.length > 0) {
    const company = await eigenerFirmenname();
    for (const mitarbeiterId of neu) {
      const { error } = await supabase.from(T_ZUORDNUNGEN).insert({
        project_id: pid,
        mitarbeiter_id: mitarbeiterId,
        bereich_id: bid,
        seit: tag,
        bis: null,
        company_name: company,
      });
      if (error && !istUniqueVerletzung(error)) return werfe(error);
    }
  }

  if (schliessen.length > 0) {
    const { error } = await supabase
      .from(T_ZUORDNUNGEN)
      .update({ bis: tag, updated_at: new Date().toISOString() })
      .in('id', schliessen)
      .is('bis', null);
    if (error) return werfe(error);
  }

  const { data, error } = await supabase
    .from(T_ZUORDNUNGEN)
    .select('*')
    .eq('bereich_id', bid)
    .order('seit', { ascending: true })
    .order('id', { ascending: true });
  if (error) return werfe(error);
  return ((data ?? []) as Zeile[]).map(mapMitarbeiterBereich);
};
